import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import SortIcon from './sort/SortIcon';
import { SortOrder } from '../../types/sort/sort';
import '../../styles/Table.css';

interface Header {
  label: string;
  sortable: boolean;
  sortKey: string;
  width: string; // px 指定
}

interface BaseTableProps {
  headers: ReadonlyArray<Header>;
  children: React.ReactNode;
  actionable?: boolean;
  onSort?: (sortKey: string) => void;
  sortOrder?: SortOrder;
  activeSortColumn?: string;
  headerRef?: React.RefObject<HTMLDivElement | null>; // 高さ計算用
  footerRef?: React.RefObject<HTMLDivElement | null>; // 高さ計算用
}

const BaseTable = ({
  headers,
  children,
  onSort,
  sortOrder,
  activeSortColumn,
  headerRef,
  footerRef,
}: BaseTableProps) => {
  // "200px" → 200 にして合計
  const headersTotalPx = headers.reduce((sum, h) => {
    const n = parseInt(h.width, 10);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  // 800 未満なら合計、以上なら 800
  const minWidthPx = headersTotalPx < 800 ? headersTotalPx : 800;

  // ウィンドウの高さに応じて maxHeight を動的に変える
  const [maxHeight, setMaxHeight] = useState('400px');
  const updateMaxHeight = () => {
    if (!headerRef || !footerRef) return; // 動作確認のためいったんエラー回避
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const footerHeight = footerRef.current?.offsetHeight || 0;
    const remainingHeight = window.innerHeight - headerHeight - footerHeight;
    setMaxHeight(`${remainingHeight * 0.78}px`);
  };

  useEffect(() => {
    window.addEventListener('resize', updateMaxHeight);
    updateMaxHeight(); // 初期計算
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);

  return (
    <div className="table-center">
      <div
        className="table-responsive table-container"
        style={{ maxHeight: maxHeight }}
      >
        <Table
          className="table-bordered base-table text-center align-middle"
          style={{
            minWidth: `${minWidthPx}px`,
            width: 'max-content',
            margin: '0 auto',
          }}
        >
          <thead className="table-header">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="bg-primary text-white align-items-center header-th"
                  style={{ width: header.width }}
                >
                  {header.label}
                  {header.sortable &&
                    onSort &&
                    activeSortColumn &&
                    sortOrder && (
                      <SortIcon
                        sortOrder={sortOrder}
                        sortColumn={header.sortKey}
                        activeSortColumn={activeSortColumn}
                        onClick={() => onSort(header.sortKey)}
                      />
                    )}
                </th>
              ))}
            </tr>
          </thead>
          {children}
        </Table>
      </div>
    </div>
  );
};

export default BaseTable;
